import moment from "moment";
import {
	find,
	orderBy,
	padEnd,
	padStart
} from "lodash";
import cloneDeep from "clone-deep";

function uiHelpers($anchorScroll, $timeout, $logger) {
	this.flash = flash;
	this.setDisplay = setDisplay;
	this.getEntityDisplay = getEntityDisplay;
	this.getLastFour = getLastFour;
	this.stringToHexColour = stringToHexColour;
	this.textColorForHexBk = textColorForHexBk;
	this.intToHexColorString = intToHexColorString;
	this.getRenderingSlots = getRenderingSlots;
	this.getDefaultEntitySlots = getDefaultEntitySlots;

	function getLastFour(item, subStringOf) {
		if(!subStringOf)
			return item;
		const subString = subStringOf.length < 5 ?
			`(${subStringOf})` :
			`(-${subStringOf.substr(subStringOf.length - 4)})`;
		return `${item} ${subString}`;
	}

	function stringToHexColour(str) {
		let hash = 0;
		for(let i = 0; i < str.length; i++) {
			hash = str.charCodeAt(i) + ((hash << 5) - hash);
		}
		let colour = "#";
		let colorNum = 0;
		for(let i = 0; i < 3; i++) {
			const value = (hash >> (i * 8)) & 0xFF;
			colour += ("00" + value.toString(16)).substr(-2);
			colorNum = (colorNum << (i * 8)) + value;
		}
		const inted = parseInt(colour.substring(1, 7), 16);
		return inted;
	}

	function intToHexColorString(num) {
		return "#" + num.toString(16);
	}

	function textColorForHexBk(hexBkColor) {
		const redVal = (hexBkColor & 0xFF0000) >> 16;
		const greenVal = (hexBkColor & 0x00FF00) >> 8;
		const blueVal = hexBkColor & 0x0000FF;

		const perceptiveLuminance = Math.round(1 - ((0.299 * redVal) + (0.587 * greenVal) + (0.114 * blueVal)) / 255) * 255;

		return (perceptiveLuminance) > 0 ? "#FFFFFF" : "#000000";
	}


	function mapItems(data, hideCode, renderingOptions, isRemoved, searchCode) {
		const getSlotDisplay = (slot) => {
			if(!slot?.hasOption || !slot?.hasData) {
				return "";
			}

			return `${slot.data}`;
		};

		const results = data.map((item) => {
			let display = item.Name || item.Code;
			let searchOptions = item.Name || item.Code;

			if(!!renderingOptions) {
				const slots = getRenderingSlots(item, renderingOptions).map(s => getSlotDisplay(s));
				
				display = `${slots[0]} ${slots[1]}<small>${slots[2]}</small>`;
				searchOptions = `${slots[0]} ${slots[1]} ${slots[2]} `;
			}
			else if(item.Name && item.Code) {
				if(!hideCode) {
					display = `${item.Name}<small>${item.Code}</small>`;
				}

				if(!hideCode || searchCode) {
					searchOptions = `${item.Code || ""} ${item.Name || ""} `;
				}
			}

			const itemIsDeactivated = item.DeactivatedAt && moment(item.DeactivatedAt).isBefore(moment());

			const isRecentBadge = "<i class=\"zmdi zmdi-time-restore\"></i>";
			const isDeactivatedBadge = "<i class=\"zmdi zmdi-alert-triangle\"></i>";

			const badge = itemIsDeactivated ? isDeactivatedBadge 
							: item.IsRecent ? isRecentBadge 
							: "";

			const effectiveRanges = item.Effective?.length
				? item.Effective
				: item.Entities?.reduce((ranges, entityLink) => {
					return ranges.concat(entityLink.Entity?.Effective || []);
				}, []);

			return {
				value: item.Id?.toString() ?? item.Code ?? item.Name,
				label: badge ? badge + " " + display : display,
				customProperties: {
					isRecent: !itemIsDeactivated && item.IsRecent,
					searchBy: searchOptions,
					isActive: item.IsActive || (item.IsActive === undefined && !itemIsDeactivated),
					isRemoved: !!isRemoved?.(item),
					deactivatedAt: itemIsDeactivated,
					effectiveRanges
				}
			};
		});

		return results;
	}

	const getAdvancedDirectiveValue = (nestedSelectedEntity, advancedRenderingOption) => {
		const getSingleRenderedValue = (nestedSelectedEntity, optionValue) => {
			if(typeof optionValue === "string" || optionValue instanceof String) {
				return optionValue;
			}

			if(
				Object.prototype.hasOwnProperty.call(optionValue, "isBold") 
				|| Object.prototype.hasOwnProperty.call(optionValue, "isItalic") 
				|| Object.prototype.hasOwnProperty.call(optionValue, "content")
			) {
				const value = getSingleRenderedValue(nestedSelectedEntity, optionValue.content);
				if(optionValue.isBold || optionValue.isItalic) {
					const styleProp = `
						style="font-weight: ${optionValue.isBold ? "bold" : "normal"}; font-style: ${optionValue.isItalic ? "italic" : "normal"}"
					`;

					return `
						<span ${styleProp}>${value}</span>
					`;
				}
			}

			const item = findDataItem(optionValue, nestedSelectedEntity);
			let dataValue = item?.data;
			if(Object.values(item?.option ?? {}).length > 0) {
				const dataModifiers = item.option;

				if(dataModifiers.padLeft !== undefined && dataModifiers.minLength !== undefined) {
					dataValue = padStart(dataValue, dataModifiers.minLength, dataModifiers.padLeft);
				}
				else if(dataModifiers.padRight !== undefined && dataModifiers.minLength !== undefined) {
					dataValue = padEnd(dataValue, dataModifiers.minLength, dataModifiers.padRight);
				}
			}

			return dataValue;
		};
		
		const renderedValues = advancedRenderingOption.map(o => getSingleRenderedValue(nestedSelectedEntity, o));

		return renderedValues.join("");
	};

	const getSlotData = (nestedSelectedEntity, renderingOption) => {
		if(Array.isArray(renderingOption)) {
			return getAdvancedDirectiveValue(nestedSelectedEntity, renderingOption);
		}

		if(Object.values(renderingOption).length > 0) {
			const foundData = findDataItem(renderingOption, nestedSelectedEntity);

			return foundData?.data;
		}

		return "";
	};

	function getRenderingSlots(nestedSelectedEntity, renderingSlotDefinitions) {
		const renderedOptionData = renderingSlotDefinitions.map(opt => {
			if(opt === undefined || Object.values(opt).length === 0) {
				return {
					data: undefined,
					hasOption: false, 
					hasData: false
				};
			}

			const data = getSlotData(nestedSelectedEntity, opt);
			const slot = {
				data,
				hasOption: true, 
				hasData: data !== undefined
			};

			if(Array.isArray(slot.data)) {
				slot.data = slot.data.join(", ");
			}

			return slot;
		});

		if(!Array.isArray(renderingSlotDefinitions[1])) {
			if(renderedOptionData[1].hasOption && renderedOptionData[1].hasData) {
				renderedOptionData[1].data = `(${renderedOptionData[1].data ?? "\u2014"})`;
			}
			else {
				renderedOptionData[1].hasData = false;
				renderedOptionData[1].hasOption = false;
			}
		}

		return renderedOptionData;
	}

	const getHtmlForDirective = (friendly, primary, renderingOption, appliedClass) => {
		const friendlyCopy = cloneDeep(friendly);
		friendlyCopy.Entities = [{ Entity: primary }];

		const [
			slot0Data,
			slot1Data,
			slot2Data,
			slot3Data
		] = getRenderingSlots(friendlyCopy, renderingOption);

		const getSlotDisplay = (slot) => {
			if(!slot?.hasOption) {
				return "";
			}

			return `${slot.data ?? "\u2014"}`;
		};

		return `
			<span class=${appliedClass}>
				<small class="f-700 truncated">
					${getSlotDisplay(slot0Data)} ${getSlotDisplay(slot1Data)}
				</small> 
				${getSlotDisplay(slot2Data)} ${getSlotDisplay(slot3Data)}
			</span>
		`;
	};

	function getDefaultEntitySlots(friendly, primary, hideCode, hidePrimary) {
		const renderingSlots = [];
		renderingSlots[0] = { 
			data: friendly.Name || friendly.Code,
			hasData: true
		};

		if(friendly.Name && friendly.Code && !hideCode) {
			renderingSlots[1] = {
				data: friendly.Code,
				hasData: true
			};
		}
		if(!primary?.Id || hidePrimary) {
			return renderingSlots;
		}

		renderingSlots[2] = {
			data: primary.Name || primary.Code || "",
			hasData: true
		};

		return renderingSlots;
	}

	const getDefaultEntityDisplay = (friendly, primary, hideCode, hidePrimary, appliedClass = "") => {
		const defaultSlots = getDefaultEntitySlots(friendly, primary, hideCode, hidePrimary);
		let friendlyDisplay = defaultSlots[0].data;

		if(defaultSlots[1]?.hasData) {
			friendlyDisplay = `${defaultSlots[0].data} (${defaultSlots[1].data})`;
		}
		if(!primary?.Id || hidePrimary) {
			return `<span class="truncated one-line" title="${friendlyDisplay}">${friendlyDisplay}</span>`;
		}

		const primaryDisplay = defaultSlots[2]?.hasData ? defaultSlots[2].data : "";

		return `
			<span class='${appliedClass} two-lines'>
				<small class="f-700 truncated">${friendlyDisplay}</small> 
				${primaryDisplay}
			</span>
		`;
	};

	function getEntityDisplay(friendly, primary, hideCode, hidePrimary, renderingOptions) {
		if(!!renderingOptions && Object.values(renderingOptions).filter(o => o !== undefined).length > 0) {
			const displayBlocks = Object.entries(renderingOptions)
				.map(([cssClass, renderingOption]) => {
					if(renderingOption !== undefined) {
						const html =  getHtmlForDirective(friendly, primary, renderingOption, cssClass);
						return html;
					}
					return getDefaultEntityDisplay(friendly, primary, hideCode, hidePrimary, cssClass);
				});

			return `<div>${displayBlocks.join("")}</div>`;
		}

		return getDefaultEntityDisplay(friendly, primary, hideCode, hidePrimary);
	}

	// type optionsType = {
	//		recentlyUsedEnabled: boolean;
	//		hideCode: boolean;
	//		searchCode: boolean;
	//		matchSpecificity: boolean;
	// }
	function setDisplay(items, options, renderingOptions) {
		if(!(items || {}).length)
			return [];

		options = options ?? {};
		const {
			hideCode, isRemoved, searchCode 
		} = options;

		const isRecentAndNotDeactivated = (item) => {
			const isNotDeactivated = !item.DeactivatedAt || moment(item.DeactivatedAt).isBefore(moment());
			if(!isNotDeactivated)
				return 1;

			return item.IsRecent ? 0 : 1;
		};

		items = orderBy(items, [isRecentAndNotDeactivated, "Order", "Name"], ["asc", "asc", "asc"]);

		const mapped = mapItems(items, hideCode, renderingOptions, isRemoved, searchCode);

		if(items.length > 11) {
			const label = `<i class="zmdi zmdi-info-outline"></i> ${items.length} total options <small>Type to filter</small>`;

			const header = {
				value: "-1",
				label: label,
				placeholder: "hi",
				disabled: true,
				customProperties: {}
			};

			mapped.unshift(header);
		}

		return mapped;
	}

	function flash(elementId) {
		$timeout(() => {
			$anchorScroll(elementId);
			const el = document.getElementById(elementId);
			angular.element(el).addClass("flash");
			$timeout(() => {
				angular.element(el).removeClass("flash");
			}, 2000);
		}, 1);
	}

	function findDataItem(renderingOption, dataItem) {
		const singleKey = Object.keys(renderingOption)[0];
		const nestedOption = renderingOption[singleKey];
		const nestedData = dataItem[singleKey];

		if(nestedData === undefined) {
			return undefined;
		}

		if(Array.isArray(nestedOption)) {
			const arrayData = findItemInArray(nestedOption[0], nestedData);
			if(!arrayData || !Array.isArray(arrayData)) {
				return arrayData;
			}

			const filteredArrayData =  arrayData.filter(d => d !== undefined)
				.map(d => d.data);

			const returnVal = {
				option: nestedOption[0],
				data: filteredArrayData.length === 0 ? undefined : filteredArrayData.join(", ")
			};

			return returnVal;
		}

		if(typeof nestedData !== "string" && typeof nestedOption !== "string" && Object.keys(nestedOption).length > 0) {
			return findDataItem(nestedOption, nestedData);
		}

		if(
			nestedData === nestedOption 
			|| Object.keys(nestedOption).length === 0 
			|| ((typeof nestedData === "string" 
				|| nestedData instanceof String)
				&& typeof nestedOption === "object")
		) {
			return { data: nestedData, option: nestedOption };
		}

		return undefined;

		function findItemInArray(keyValOption, dataItemArray) {
			if(!!keyValOption.Key) {
				const foundDataItem = find(dataItemArray, d => {
					const foundData = findDataItem(keyValOption.Key, d);

					return foundData;
				});

				return foundDataItem !== undefined ? findDataItem(keyValOption.Value, foundDataItem) : "";
			}
			else {
				const items = dataItemArray.map(d => {
					return findDataItem(keyValOption.Value, d);
				});

				if(items.length === 1) {
					return items[0];
				}

				return items;
			}
		}
	}
}

export default angular
	.module("vesl.services.ui-helpers", [])
	.service("uiHelpers", uiHelpers).name;
